import { useSeo } from "@/lib/seo";
import { Overline, Reveal, Section } from "@/components/shared";
import { PageHero, FAQ, StepList } from "@/components/PageParts";
import { PricingCard } from "@/components/PricingCard";
import { Compass, Layout, Workflow, LifeBuoy } from "lucide-react";

const components = [
  "Website", "Marketing", "Operations", "Customers", "Team", "Tools", "Finances", "Communication",
];

const provide = [
  { icon: Compass, title: "Business and Digital Consulting", desc: "Strategy that treats your business as an interconnected system." },
  { icon: Layout, title: "Website and Marketing Implementation", desc: "Websites and marketing that are built to convert, not just exist." },
  { icon: Workflow, title: "Automations and Digital Ecosystem Setup", desc: "Connected tools and processes that reduce friction and manual work." },
  { icon: LifeBuoy, title: "Ongoing Maintenance and Support", desc: "Continuous improvement, monitoring and technical support." },
];

const howWeWork = [
  { t: "Understand" }, { t: "Strategize" }, { t: "Implement" }, { t: "Support" },
];

const included = [
  "One active project at a time",
  "Strategy",
  "Website design or improvement",
  "Marketing implementation",
  "Automation setup",
  "Digital ecosystem setup",
  "Maintenance and support",
  "Monthly prioritization",
];

const faqs = [
  { q: "What is included?", a: "Strategy, website design or improvement, marketing implementation, automation and digital ecosystem setup, plus ongoing maintenance and support — prioritized monthly." },
  { q: "Is everything included in one monthly subscription?", a: "Yes. Our work is delivered through a single monthly retainer with one active project at a time and clear monthly priorities." },
  { q: "Do I need an existing website?", a: "No. We can design a new website or improve an existing one as part of the engagement." },
  { q: "Can you implement the strategy?", a: "Yes. We connect strategy with real implementation across websites, marketing, automations and tools." },
  { q: "What types of businesses do you work with?", a: "Small and medium enterprises across sectors that want a coherent, connected digital ecosystem." },
  { q: "How do you use a systems approach?", a: "We map how your website, marketing, operations, customers, team, tools and finances interact, then improve the whole rather than isolated parts." },
  { q: "Are requests unlimited?", a: "We work on one active project at a time with monthly prioritization, which keeps focus and quality high." },
  { q: "What external costs are not included?", a: "Advertising budgets, paid software, hosting, AI subscriptions, CRMs, email tools and third-party platforms are billed separately." },
  { q: "How do we get started?", a: "Book a business consultation. We assess your current system, define priorities and begin implementation." },
];

export default function ForSMEs() {
  useSeo(
    "Digital Systems and Automation for SMEs | Systems Beyond",
    "Systems Beyond helps SMEs connect strategy, websites, automations, marketing and digital tools into coherent business systems."
  );

  return (
    <div>
      <PageHero
        overline="For SMEs"
        title="Digital systems and automation for SMEs."
        subtitle="We help small and medium businesses connect strategy, websites, marketing, automation and operations into one coherent digital ecosystem."
      />

      <Section className="py-16 lg:py-24" grid>
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Overline>A systems view</Overline>
            <h2 className="mt-6 text-3xl lg:text-4xl tracking-tight leading-tight font-medium text-ink">
              Every business is a complex system.
            </h2>
            <p className="mt-6 text-dark-gray leading-relaxed">
              Website, marketing, operations, customers, team, tools, finances and communication all
              interact. Outcomes emerge from how these parts connect.
            </p>
          </div>
          <div className="lg:col-span-7 flex flex-wrap gap-2 items-start content-start">
            {components.map((c) => (
              <span key={c} className="font-mono-sci text-xs text-dark-gray border border-silver px-4 py-2.5 hover:border-acid transition-colors">{c}</span>
            ))}
          </div>
        </div>
      </Section>

      <Section dark className="py-20 lg:py-28">
        <div className="max-w-2xl">
          <Overline dark>The problem with isolated solutions</Overline>
          <h2 className="mt-6 text-3xl lg:text-4xl tracking-tight leading-tight font-medium">
            Disconnected tools leak value.
          </h2>
        </div>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
          {[
            "A website without strategy does not sell.",
            "Marketing without infrastructure leaks leads.",
            "Automations without process create chaos.",
            "Tools without integration increase complexity.",
          ].map((t, i) => (
            <Reveal key={i} delay={i * 90}>
              <div className="bg-charcoal p-8 h-full">
                <span className="w-2 h-2 bg-acid rounded-full inline-block" />
                <p className="mt-5 text-white/80 leading-relaxed">{t}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section className="py-20 lg:py-28">
        <Overline>What we provide</Overline>
        <div className="mt-10 grid sm:grid-cols-2 gap-px bg-silver/40 border border-silver/40">
          {provide.map((p, i) => (
            <Reveal key={p.title} delay={i * 90}>
              <div className="bg-white p-8 h-full group hover:bg-off-white transition-colors">
                <div className="w-11 h-11 border border-silver flex items-center justify-center group-hover:border-acid transition-colors">
                  <p.icon size={18} strokeWidth={1.5} className="text-ink" />
                </div>
                <h3 className="mt-6 text-xl text-ink">{p.title}</h3>
                <p className="mt-3 text-sm text-dark-gray leading-relaxed">{p.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section className="pb-20 lg:pb-28">
        <Overline>How we work</Overline>
        <div className="mt-10"><StepList steps={howWeWork} /></div>
      </Section>

      <Section className="pb-20 lg:pb-28">
        <div className="max-w-2xl mb-10">
          <Overline>Ongoing digital systems support</Overline>
          <h2 className="mt-6 text-3xl lg:text-4xl tracking-tight leading-tight font-medium text-ink">A coherent monthly partnership.</h2>
        </div>
        <PricingCard
          overline="DIGITAL RETAINER"
          title="Ongoing digital systems support"
          price="$1,500"
          planId="smes"
          consultCta="Book a Business Consultation"
          includes={included}
          exclusion="External costs such as advertising budgets, paid software, hosting, AI subscriptions, CRMs, email tools or third-party platforms are billed separately."
        />
      </Section>

      <Section className="pb-20 lg:pb-28">
        <div className="max-w-2xl mb-10">
          <Overline>FAQ</Overline>
          <h2 className="mt-6 text-3xl lg:text-4xl tracking-tight leading-tight font-medium text-ink">Working with SMEs.</h2>
        </div>
        <FAQ items={faqs} />
      </Section>
    </div>
  );
}
